import { addDays, eachDayOfInterval, format, parseISO } from 'date-fns';

export function getDaysInRange(start: string, end: string): string[] {
  const startDate = parseISO(start);
  const endDate = parseISO(end);
  return eachDayOfInterval({ start: startDate, end: endDate }).map((day) => format(day, 'yyyy-MM-dd'));
}

export function splitDateRangeByDays(start: string, end: string, chunkSize = 4): { start: string; end: string }[] {
  const result: { start: string; end: string }[] = [];
  let current = parseISO(start);
  const endDate = parseISO(end);
  while (current <= endDate) {
    const chunkEnd = addDays(current, chunkSize - 1);
    result.push({
      start: format(current, 'yyyy-MM-dd'),
      // eslint-disable-next-line unicorn/prefer-math-min-max -- Date operands; Math.min returns a number and NaN on invalid dates
      end: format(chunkEnd <= endDate ? chunkEnd : endDate, 'yyyy-MM-dd')
    });
    current = addDays(chunkEnd, 1);
  }
  return result;
}

export async function promisePoolForTasks<T>(tasks: (() => Promise<T>)[], maxConcurrency: number): Promise<T[]> {
  const results: T[] = [];
  let i = 0;
  const run = async () => {
    while (i < tasks.length) {
      const cur = i++;
      try {
        results[cur] = await tasks[cur]();
      } catch (error) {
        console.log(error);
        results[cur] = undefined as any;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(maxConcurrency, tasks.length) }, run));
  return results;
}

/** Follows `next` cursors until exhausted, keeping the first copy of each id; stops if a cursor repeats. */
export async function fetchAllPages<T extends { id: string }>(
  fetchPage: (next: string | undefined) => Promise<{ items: readonly (T | null | undefined)[]; next?: string | null }>
): Promise<T[]> {
  const seenIds = new Set<string>();
  const seenCursors = new Set<string>();
  const items: T[] = [];
  let cursor: string | undefined;

  do {
    const page = await fetchPage(cursor);
    for (const item of page.items) {
      if (item?.id && !seenIds.has(item.id)) {
        seenIds.add(item.id);
        items.push(item);
      }
    }
    const next = page.next ?? undefined;
    cursor = next && !seenCursors.has(next) ? next : undefined;
    if (cursor) seenCursors.add(cursor);
  } while (cursor);

  return items;
}
